import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { User, Mail, Lock, CheckCircle, AlertCircle, Loader2, Save } from 'lucide-react';
import { motion } from 'framer-motion';

export default function InfluencerProfile() {
  const { user, refreshUser } = useAuth();
  const [pseudo, setPseudo] = useState(user?.pseudo || user?.full_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileMsg(null);
    try {
      const updates = {};
      if (pseudo !== (user?.pseudo || user?.full_name)) updates.pseudo = pseudo;
      if (email !== user?.email) updates.email = email;
      if (!Object.keys(updates).length) { setProfileMsg({ type: 'info', text: 'Aucune modification' }); setProfileLoading(false); return; }
      await api.put('/influencer/profile', updates);
      setProfileMsg({ type: 'success', text: 'Profil mis à jour' });
      refreshUser?.();
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setPwMsg({ type: 'error', text: 'Les mots de passe ne correspondent pas' }); return; }
    if (newPw.length < 8) { setPwMsg({ type: 'error', text: 'Min. 8 caractères' }); return; }
    setPwLoading(true);
    setPwMsg(null);
    try {
      await api.put('/influencer/password', { current_password: currentPw, new_password: newPw });
      setPwMsg({ type: 'success', text: 'Mot de passe modifié' });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    } finally {
      setPwLoading(false);
    }
  };

  const MsgBox = ({ msg }) => {
    if (!msg) return null;
    const colors = { success: 'border-[#00FF88]/30 bg-[#00FF88]/10 text-[#00FF88]', error: 'border-[#FF3B5C]/30 bg-[#FF3B5C]/10 text-[#FF3B5C]', info: 'border-[#FFB800]/30 bg-[#FFB800]/10 text-[#FFB800]' };
    return (
      <div className={`p-3 rounded-lg border flex items-center gap-2 ${colors[msg.type]}`}>
        {msg.type === 'success' ? <CheckCircle className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
        <span className="text-sm">{msg.text}</span>
      </div>
    );
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-2xl" data-testid="influencer-profile-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><User className="h-6 w-6 text-[#FF6B35]" />Mon Profil</h1>
        <p className="text-[#6B6B6E] text-sm mt-1">Gérez vos informations</p>
      </div>

      {/* Profile Form */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#1A1A1D] p-6 rounded-lg border border-[#3A3A3D]/40">
        <h2 className="text-lg font-bold text-white mb-4">Informations</h2>
        <form onSubmit={handleProfileSave} className="space-y-4">
          <div>
            <label className="text-[#B8B8BA] text-xs font-semibold uppercase mb-1 block">Pseudo</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B6B6E]" />
              <input type="text" value={pseudo} onChange={e => setPseudo(e.target.value)} required
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="profile-pseudo" />
            </div>
          </div>
          <div>
            <label className="text-[#B8B8BA] text-xs font-semibold uppercase mb-1 block">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B6B6E]" />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="profile-email" />
            </div>
          </div>
          <MsgBox msg={profileMsg} />
          <button type="submit" disabled={profileLoading} className="btn-primary py-2.5 px-6 flex items-center gap-2 disabled:opacity-50" data-testid="profile-save">
            {profileLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Enregistrer
          </button>
        </form>
      </motion.div>

      {/* Password Form */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-[#1A1A1D] p-6 rounded-lg border border-[#3A3A3D]/40">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Lock className="h-5 w-5 text-[#FF6B35]" />Changer le mot de passe</h2>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="text-[#B8B8BA] text-xs font-semibold uppercase mb-1 block">Mot de passe actuel</label>
            <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} required
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="pw-current" />
          </div>
          <div>
            <label className="text-[#B8B8BA] text-xs font-semibold uppercase mb-1 block">Nouveau mot de passe</label>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required minLength={8}
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="pw-new" />
          </div>
          <div>
            <label className="text-[#B8B8BA] text-xs font-semibold uppercase mb-1 block">Confirmer</label>
            <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="pw-confirm" />
          </div>
          <MsgBox msg={pwMsg} />
          <button type="submit" disabled={pwLoading} className="btn-primary py-2.5 px-6 flex items-center gap-2 disabled:opacity-50" data-testid="pw-save">
            {pwLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}Modifier
          </button>
        </form>
      </motion.div>
    </div>
  );
}
